import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Github } from "lucide-react";
import { PROJECTS } from "../../data/content";

/**
 * Editorial "product photography" — typographic + line-work SVG art
 * that replaces stock imagery. Deliberate spotlight, clipped frames.
 */
const Art = ({ variant }) => {
    if (variant === "proctor") {
        return (
            <svg viewBox="0 0 800 500" className="w-full h-full" aria-hidden>
                <defs>
                    <linearGradient id="pa" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0" stopColor="var(--surface)" />
                        <stop offset="1" stopColor="var(--page)" />
                    </linearGradient>
                </defs>
                <rect width="800" height="500" fill="url(#pa)" />
                {/* Grid */}
                <g stroke="currentColor" strokeWidth="1" opacity="0.15">
                    {Array.from({ length: 10 }).map((_, i) => (
                        <line
                            key={`v${i}`}
                            x1={i * 80}
                            y1="0"
                            x2={i * 80}
                            y2="500"
                        />
                    ))}
                    {Array.from({ length: 7 }).map((_, i) => (
                        <line
                            key={`h${i}`}
                            x1="0"
                            y1={i * 80}
                            x2="800"
                            y2={i * 80}
                        />
                    ))}
                </g>
                {/* Camera reticle */}
                <g transform="translate(400 250)" stroke="currentColor" fill="none" strokeWidth="2">
                    <circle r="120" />
                    <circle r="80" />
                    <circle r="40" />
                    <line x1="-160" y1="0" x2="-140" y2="0" />
                    <line x1="140" y1="0" x2="160" y2="0" />
                    <line x1="0" y1="-160" x2="0" y2="-140" />
                    <line x1="0" y1="140" x2="0" y2="160" />
                </g>
                {/* Bounding box */}
                <g stroke="var(--brand)" strokeWidth="2" fill="none">
                    <rect x="500" y="90" width="180" height="220" />
                    <text
                        x="500"
                        y="82"
                        fontFamily="JetBrains Mono, monospace"
                        fontSize="11"
                        fill="var(--brand)"
                        letterSpacing="1.5"
                    >
                        FACE · 0.98
                    </text>
                </g>
                <text
                    x="30"
                    y="470"
                    fontFamily="JetBrains Mono, monospace"
                    fontSize="11"
                    fill="currentColor"
                    letterSpacing="1.5"
                    opacity="0.7"
                >
                    PROCTOR — LIVE FEED
                </text>
            </svg>
        );
    }
    if (variant === "rtx") {
        return (
            <svg viewBox="0 0 800 500" className="w-full h-full" aria-hidden>
                <rect width="800" height="500" fill="var(--surface)" />
                {/* Document stack */}
                <g stroke="currentColor" strokeWidth="2" fill="var(--surface)">
                    <rect x="200" y="120" width="240" height="300" transform="rotate(-6 320 270)" />
                    <rect x="240" y="100" width="240" height="300" transform="rotate(2 360 250)" />
                    <rect x="280" y="130" width="240" height="300" />
                    <line x1="300" y1="180" x2="500" y2="180" />
                    <line x1="300" y1="210" x2="500" y2="210" />
                    <line x1="300" y1="240" x2="460" y2="240" />
                    <line x1="300" y1="270" x2="500" y2="270" />
                    <line x1="300" y1="300" x2="440" y2="300" />
                </g>
                {/* Vector cluster */}
                <g transform="translate(620 250)" stroke="var(--brand)" fill="var(--brand)">
                    {Array.from({ length: 22 }).map((_, i) => {
                        const a = (i / 22) * Math.PI * 2;
                        const r = 40 + (i % 3) * 30;
                        const x = Math.cos(a) * r;
                        const y = Math.sin(a) * r;
                        return <circle key={i} cx={x} cy={y} r="2.5" />;
                    })}
                    <circle r="6" fill="var(--brand)" />
                </g>
                <text
                    x="30"
                    y="470"
                    fontFamily="JetBrains Mono, monospace"
                    fontSize="11"
                    fill="currentColor"
                    letterSpacing="1.5"
                    opacity="0.7"
                >
                    CHATRTX — RAG · 1024D EMBEDDINGS
                </text>
            </svg>
        );
    }
    if (variant === "neuralnet") {
        const layers = [
            [120, 190, 260, 330, 400],
            [160, 250, 340],
            [200, 300],
            [250],
        ];
        const xs = [140, 340, 540, 700];
        return (
            <svg viewBox="0 0 800 500" className="w-full h-full" aria-hidden>
                <rect width="800" height="500" fill="var(--surface)" />
                {/* Connections */}
                <g stroke="currentColor" strokeWidth="1" opacity="0.25">
                    {layers.slice(0, -1).map((layer, li) =>
                        layer.map((y1, a) =>
                            layers[li + 1].map((y2, b) => (
                                <line
                                    key={`${li}-${a}-${b}`}
                                    x1={xs[li]}
                                    y1={y1}
                                    x2={xs[li + 1]}
                                    y2={y2}
                                />
                            )),
                        ),
                    )}
                </g>
                {/* Nodes */}
                {layers.map((layer, li) =>
                    layer.map((y, a) => (
                        <circle
                            key={`n-${li}-${a}`}
                            cx={xs[li]}
                            cy={y}
                            r="12"
                            fill={
                                li === layers.length - 1
                                    ? "var(--brand)"
                                    : "var(--surface)"
                            }
                            stroke={
                                li === layers.length - 1
                                    ? "var(--brand)"
                                    : "currentColor"
                            }
                            strokeWidth="2"
                        />
                    )),
                )}
                <text
                    x="30"
                    y="470"
                    fontFamily="JetBrains Mono, monospace"
                    fontSize="11"
                    fill="currentColor"
                    letterSpacing="1.5"
                    opacity="0.7"
                >
                    CHARNET — 2 HIDDEN LAYERS · 94 CLASSES
                </text>
                <text
                    x="655"
                    y="240"
                    fontFamily="Cormorant Garamond, serif"
                    fontSize="64"
                    fontStyle="italic"
                    fill="var(--brand)"
                >
                    A
                </text>
            </svg>
        );
    }
    if (variant === "currency") {
        return (
            <svg viewBox="0 0 800 500" className="w-full h-full" aria-hidden>
                <rect width="800" height="500" fill="var(--surface)" />
                {/* Banknote */}
                <g
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    transform="rotate(-7 400 250)"
                >
                    <rect x="230" y="150" width="340" height="200" rx="4" />
                    <rect x="250" y="170" width="300" height="160" rx="2" opacity="0.4" />
                    <circle cx="300" cy="250" r="34" opacity="0.5" />
                    <text
                        x="470"
                        y="285"
                        fontFamily="Cormorant Garamond, serif"
                        fontSize="120"
                        fill="var(--brand)"
                        stroke="none"
                    >
                        ₹
                    </text>
                </g>
                {/* Detection box */}
                <g stroke="var(--brand)" strokeWidth="2" fill="none">
                    <rect x="200" y="120" width="410" height="260" />
                    <text
                        x="200"
                        y="112"
                        fontFamily="JetBrains Mono, monospace"
                        fontSize="12"
                        fill="var(--brand)"
                        letterSpacing="1.5"
                    >
                        ₹500 · 0.94
                    </text>
                </g>
                {/* Audio waves */}
                <g stroke="currentColor" strokeWidth="2" fill="none" opacity="0.6">
                    <path d="M 660 210 q 24 40 0 80" />
                    <path d="M 682 195 q 40 55 0 110" />
                    <path d="M 704 182 q 55 68 0 136" />
                </g>
                <text
                    x="30"
                    y="470"
                    fontFamily="JetBrains Mono, monospace"
                    fontSize="11"
                    fill="currentColor"
                    letterSpacing="1.5"
                    opacity="0.7"
                >
                    NOTESENSE — HI / EN AUDIO · ~2s
                </text>
            </svg>
        );
    }
    if (variant === "gesture") {
        return (
            <svg viewBox="0 0 800 500" className="w-full h-full" aria-hidden>
                <rect width="800" height="500" fill="var(--surface)" />

                {/* Dotted motion trail */}
                <path
                    d="M 150 380 C 300 320, 320 200, 470 200 S 620 280, 640 180"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeDasharray="2 12"
                    strokeLinecap="round"
                    opacity="0.55"
                />

                {/* Pinch gesture — thumb + index tips with click ripple */}
                <g>
                    <circle
                        cx="150"
                        cy="380"
                        r="46"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeDasharray="4 8"
                        opacity="0.45"
                    />
                    {/* thumb tip */}
                    <circle cx="128" cy="372" r="11" fill="currentColor" />
                    {/* index tip */}
                    <circle cx="170" cy="366" r="11" fill="currentColor" />
                    {/* connecting pinch arc */}
                    <path
                        d="M 128 372 Q 149 350 170 366"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                    />
                    <text
                        x="105"
                        y="440"
                        fontFamily="JetBrains Mono, monospace"
                        fontSize="12"
                        fill="currentColor"
                        letterSpacing="1.5"
                        opacity="0.7"
                    >
                        PINCH · CLICK
                    </text>
                </g>

                {/* Cursor arrow at the end of the trail */}
                <g transform="translate(628 150)">
                    <path
                        d="M0 0 L0 52 L14 39 L23 60 L34 55 L24 34 L46 34 Z"
                        fill="var(--ink)"
                        stroke="var(--surface)"
                        strokeWidth="2"
                        strokeLinejoin="round"
                    />
                </g>

                <text
                    x="30"
                    y="470"
                    fontFamily="JetBrains Mono, monospace"
                    fontSize="11"
                    fill="currentColor"
                    letterSpacing="1.5"
                    opacity="0.7"
                >
                    AIRCURSOR — HAND → CURSOR · MOVE · CLICK · SCROLL
                </text>
            </svg>
        );
    }
    // glasses
    return (
        <svg viewBox="0 0 800 500" className="w-full h-full" aria-hidden>
            <rect width="800" height="500" fill="var(--surface)" />
            <g stroke="currentColor" strokeWidth="2" fill="none">
                {/* Glasses outline */}
                <circle cx="290" cy="260" r="90" />
                <circle cx="510" cy="260" r="90" />
                <path d="M 380 260 C 390 240, 410 240, 420 260" />
                <line x1="200" y1="230" x2="140" y2="200" />
                <line x1="600" y1="230" x2="660" y2="200" />
                {/* Camera dot */}
                <circle cx="290" cy="215" r="6" fill="var(--brand)" stroke="var(--brand)" />
            </g>
            {/* Sound waves */}
            <g stroke="var(--brand)" strokeWidth="2" fill="none" opacity="0.9">
                <path d="M 620 100 q 30 40 0 80" />
                <path d="M 640 90 q 45 50 0 100" />
                <path d="M 660 80 q 60 60 0 120" />
            </g>
            <text
                x="30"
                y="470"
                fontFamily="JetBrains Mono, monospace"
                fontSize="11"
                fill="currentColor"
                letterSpacing="1.5"
                opacity="0.7"
            >
                SMART GLASSES — LATENCY 12s
            </text>
        </svg>
    );
};

const ProjectCard = ({ p, i }) => {
    return (
        <motion.article
            data-testid={`project-card-${p.id}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -4 }}
            className="group relative flex flex-col border-hard-3 bg-[var(--page)] hard-shadow overflow-hidden"
        >
            {/* Chapter number */}
            <div className="absolute z-20 top-3 left-3 mono-meta !text-[10px]">
                PROJECT · 0{i + 1}
            </div>
            <div className="absolute z-20 top-3 right-3 mono-meta !text-[10px] text-[var(--brand)] font-semibold">
                {p.year}
            </div>

            {/* Art */}
            <div className="relative border-b-2 border-[var(--ink)] aspect-[16/10] overflow-hidden text-[var(--ink)]">
                <div className="absolute inset-0 spotlight z-10 pointer-events-none" />
                <motion.div
                    className="absolute inset-0"
                    initial={{ scale: 1.02 }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Art variant={p.art} />
                </motion.div>
            </div>

            {/* Body */}
            <div className="p-5 md:p-7 flex-1 flex flex-col">
                <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-serif text-3xl md:text-4xl tracking-[-0.02em] leading-tight">
                        {p.name}
                    </h3>
                    <ArrowUpRight
                        className="w-5 h-5 shrink-0 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                        strokeWidth={2}
                    />
                </div>
                <div className="mono-label mt-1 text-[var(--ink-soft)]">
                    {p.subtitle}
                </div>

                <p className="mt-4 font-sans text-[15px] leading-relaxed text-[var(--ink-soft)]">
                    {p.summary}
                </p>

                <div className="mt-5 flex flex-wrap gap-1.5">
                    {p.stack.map((s) => (
                        <span
                            key={s}
                            className="mono-label !text-[10px] border-2 border-[var(--ink)] px-2 py-1 bg-[var(--surface)]"
                        >
                            {s}
                        </span>
                    ))}
                </div>

                {(p.demo || p.github) && (
                    <div className="mt-6 pt-5 border-t-2 border-[var(--ink)] flex items-center gap-3">
                        {p.demo && (
                            <a
                                data-testid={`project-demo-${p.id}`}
                                href={p.demo}
                                target={p.demo.startsWith("http") ? "_blank" : undefined}
                                rel={p.demo.startsWith("http") ? "noopener noreferrer" : undefined}
                                className="inline-flex items-center gap-2 h-9 px-3 border-2 border-[var(--brand)] bg-[var(--brand)] text-[var(--page)] hover:bg-[var(--surface)] hover:text-[var(--ink)] transition-colors"
                            >
                                <span className="mono-label !text-[10px] !text-current">
                                    View Demo
                                </span>
                                <ArrowUpRight
                                    className="w-3.5 h-3.5"
                                    strokeWidth={2.4}
                                />
                            </a>
                        )}
                        {p.github && (
                            <a
                                data-testid={`project-github-${p.id}`}
                                href={p.github}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 h-9 px-3 border-hard bg-[var(--surface)] hover:bg-[var(--ink)] hover:text-[var(--page)] transition-colors"
                            >
                                <Github className="w-3.5 h-3.5" strokeWidth={2} />
                                <span className="mono-label !text-[10px] !text-current">
                                    Code
                                </span>
                            </a>
                        )}
                    </div>
                )}
            </div>
        </motion.article>
    );
};

export const Projects = () => {
    return (
        <section
            id="projects"
            data-testid="projects-section"
            className="relative border-t-2 border-[var(--ink)] bg-[var(--surface)] pt-24 md:pt-36 pb-24 md:pb-32"
        >
            <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="grid grid-cols-12 gap-4 md:gap-6 pb-12 md:pb-16"
                >
                    <div className="col-span-12 md:col-span-6">
                        <div className="mono-label mb-4 flex items-center gap-2">
                            <span className="inline-block w-2.5 h-2.5 bg-[var(--brand)]" />
                            Ch. 05 — Projects
                        </div>
                        <h2 className="font-serif text-[clamp(2.2rem,5.5vw,4.25rem)] leading-[0.95] tracking-[-0.02em]">
                            Things I&rsquo;ve built.
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-5 md:col-start-8 md:pt-4">
                        <p className="font-sans text-base md:text-lg text-[var(--ink-soft)] leading-relaxed max-w-lg">
                            A few things I&rsquo;ve shipped — from real-time
                            computer vision to offline RAG and assistive
                            hardware. Built to work, not just to demo.
                        </p>
                    </div>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
                    {PROJECTS.map((p, i) => (
                        <ProjectCard key={p.id} p={p} i={i} />
                    ))}
                </div>
            </div>
        </section>
    );
};
